// todo: temporary
/* eslint-disable tailwindcss/no-arbitrary-value */
import type React from "react";
import {
	forwardRef,
	type HTMLAttributes,
	type ReactNode,
	type SyntheticEvent,
	type TextareaHTMLAttributes,
	useEffect,
	useRef,
	useState,
} from "react";
import { tw } from "../../../styling";
import { Text } from "../../typography";

const InputElement = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
	({ className, id, ...rest }, ref) => (
		<textarea
			id={id}
			ref={ref}
			{...rest}
			className={tw(
				`border-text-disabled hover:border-interactive-primary-hover focus:border-interactive-primary-active focus:placeholder:text-text-disabled disabled:bg-background-tertiary disabled:text-text-disabled resize-none rounded-lg border border-solid px-4 pt-4 pb-2 outline-hidden transition-colors placeholder:text-transparent focus:border-2 focus:px-[15px] focus:pt-[15px] focus:pb-[7px]`,
				className,
			)}
		/>
	),
);
InputElement.displayName = "InputElement";

type WrapperProps = HTMLAttributes<HTMLDivElement> & {
	children: React.ReactNode;
};
const WrapperElement = ({ children, ...rest }: WrapperProps) => <div {...rest}>{children}</div>;

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
	label?: string;
	type?: "number" | "text" | "email" | "password" | "tel";
	id?: string;
	error?: boolean;
	fullWidth?: boolean;
	endAdornment?: ReactNode;
};
export const TextAreaInput = ({
	value,
	label,
	id,
	fullWidth = false,
	error = false,
	endAdornment,
	maxLength,
	disabled,
	onBlur,
	onFocus,
	...rest
}: TextAreaProps) => {
	const inputElement = useRef<HTMLTextAreaElement>(null);
	const adornmentRef = useRef<HTMLDivElement>(null);
	const [hasFocus, setHasFocus] = useState(false);
	const [moveLabel, setMoveLabel] = useState(false);
	const [currentLength, setCurrentLength] = useState(value?.toString()?.length || 0);

	useEffect(() => {
		setMoveLabel(hasFocus || !!value);
	}, [hasFocus, value]);

	const adornmentStyle =
		endAdornment || maxLength
			? {
					paddingInlineEnd: (adornmentRef.current?.clientWidth || 0) + 8,
				}
			: {};

	return (
		<WrapperElement className={tw("inline-flex", fullWidth && "w-full")}>
			<label className={tw("relative flex flex-row items-baseline", fullWidth && "w-full")}>
				<div
					className={tw(
						"pointer-events-none absolute top-3 left-4 origin-left cursor-text transition-transform",
						moveLabel && "translate-y-[-12px] scale-75 whitespace-nowrap",
						error && "text-foreground-critical",
						disabled && "text-text-disabled",
					)}
				>
					{label}
				</div>
				<InputElement
					style={adornmentStyle}
					onChangeCapture={(e: SyntheticEvent<HTMLTextAreaElement>) => {
						setCurrentLength(e.currentTarget.value.length);
					}}
					id={id}
					onFocus={(e) => {
						setHasFocus(true);
						onFocus?.(e);
					}}
					onBlur={(e) => {
						setHasFocus(false);
						onBlur?.(e);
					}}
					className={tw(
						fullWidth && "w-full",
						error &&
							"border-foreground-critical hover:border-foreground-critical focus:border-foreground-critical",
					)}
					ref={inputElement}
					value={value}
					maxLength={maxLength}
					disabled={disabled}
					{...rest}
				/>

				{(endAdornment || maxLength) && (
					<div
						ref={adornmentRef}
						className={tw(
							"absolute right-2 flex items-center justify-center pt-0 pr-2 transition-all",
							(hasFocus || value) && "pt-2",
						)}
					>
						{endAdornment}
						{maxLength && (
							<Text bold className="text-interactive-primary text-sm">
								{currentLength} / {maxLength}
							</Text>
						)}
					</div>
				)}
			</label>
		</WrapperElement>
	);
};
